import React, { useState, useMemo } from 'react';
import {
  Grid,
  Box,
  Autocomplete,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { monthlyClientDashboard } from 'src/api/reports';
import { getClientProperties } from 'src/api/ShiftMaster';
import { getClientGroupInfo } from 'src/api/liveCameras';
import FormComponent from 'src/templates/MonthlyClientTemplate';

const currentYear = new Date().getFullYear();

const months = Array.from({ length: 12 }, (_, idx) => {
  const date = new Date(currentYear, idx, 1);
  return {
    name: date.toLocaleString('default', { month: 'long' }),
    value: (idx + 1).toString(),
  };
});

const years = [
  { name: (currentYear - 1).toString(), value: currentYear - 1 },
  { name: currentYear.toString(), value: currentYear },
  { name: (currentYear + 1).toString(), value: currentYear + 1 },
];

const normalizeDisplayName = (name) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const buildUniqueClientOptions = (clients) => {
  const grouped = new Map();

  (clients || []).forEach((client) => {
    const key = normalizeDisplayName(client?.displayname);
    if (!key) return;

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(client);
  });

  return Array.from(grouped.values()).map((groupItems) => {
    if (groupItems.length === 1) return groupItems[0];

    const assignTargets = new Set(
      groupItems
        .map((item) => Number(item.AssignClient || 0))
        .filter((assignId) => assignId > 0)
    );

    const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
    if (mappedMain) return mappedMain;

    const rootEntry = groupItems.find((item) => Number(item.AssignClient || 0) === 0);
    return rootEntry || groupItems[0];
  });
};

const MonthlyClientDashBoard = ({ clientList }) => {
  const [value1, setValue1] = useState(null);
  const [value2, setValue2] = useState(null);
  const [value3, setValue3] = useState(null);
  const [value4, setValue4] = useState(null);

  const [data, setData] = useState([]);
  const [showChart, setShowChart] = useState(false);
  const [graphData, setGrapghData] = useState([]);
  const [noData, setNoData] = useState(false);
  const [cameraWiseData, setCameraWiseData] = useState([]);

  const [error, setError] = useState(false);
  const [isMonthSelected, setIsMonthSelected] = useState(false);
  const [isYearSelected, setIsYearSelected] = useState(false);
  const [isClientSelected, setIsClientSelected] = useState(false);

  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [loading, setLoading] = useState(false);

  const uniqueClientOptions = useMemo(
    () => buildUniqueClientOptions(clientList || []),
    [clientList]
  );

  const selectedLocationOption = useMemo(
    () => locations.find((loc) => selectedLocations.includes(loc.ID || loc.id)) || null,
    [locations, selectedLocations]
  );
  const showLocationDropdown = Boolean(value3 && locations.length > 0);
  const showGroupDropdown = groups.length >= 1;
  const compactLayout = showLocationDropdown && showGroupDropdown;

  const loadGroups = (selectedClientId, selectedLocationIds = []) => {
    if (!selectedClientId) {
      setGroups([]);
      setValue4(null);
      return;
    }

    const body = {
      clientId: selectedLocationIds.length > 0 ? selectedLocationIds.join(',') : selectedClientId,
      groupId: 0,
    };

    getClientGroupInfo(body, (response) => {
      if (response.status === 200 && response.data.length > 0) {
        const uniqueGroups = Array.from(
          new Map(
            response.data.map((item) => [
              item.GroupId,
              { Id: item.GroupId, GroupName: item.GroupName },
            ])
          ).values()
        );
        setGroups(uniqueGroups);
      } else {
        setGroups([]);
        setValue4(null);
      }
    });
  };

  const loadClientContext = (newValue) => {
    setValue3(newValue);
    setValue4(null);
    setIsClientSelected(false);
    setShowChart(false);
    setNoData(false);

    if (!newValue) {
      setValue1(null);
      setValue2(null);
      setIsMonthSelected(false);
      setIsYearSelected(false);
      setValue4(null);
      setGroups([]);
      setLocations([]);
      setSelectedLocations([]);
      setData([]);
      setGrapghData([]);
      setCameraWiseData([]);
      setError(false);
      return;
    }

    getClientProperties({ AssignClient: String(newValue.ID) }, (response) => {
      if (response.status === 200) {
        const normalizedLocations = Array.from(
          new Map(
            (response.data || [])
              .map((location) => ({
                ...location,
                ID: Number(location?.ID ?? location?.id ?? 0),
                Location: String(location?.Location || location?.name || '').trim(),
              }))
              .filter((location) => location.ID > 0)
              .map((location) => [location.ID, location])
          ).values()
        );

        setLocations(normalizedLocations);
        setSelectedLocations([]);

        if (normalizedLocations.length > 0) {
          setGroups([]);
          setValue4(null);
          return;
        }
      } else {
        setGroups([]);
        setValue4(null);
        setLocations([]);
        setSelectedLocations([]);
      }

      loadGroups(newValue.ID, []);
    });
  };

  const handleLocationChange = (event, value) => {
    const nextSelectedLocations = value ? [value.ID || value.id] : [];

    setSelectedLocations(nextSelectedLocations);
    setValue4(null);
    setShowChart(false);
    setNoData(false);

    if (locations.length > 0 && nextSelectedLocations.length === 0) {
      setValue1(null);
      setValue2(null);
      setIsMonthSelected(false);
      setIsYearSelected(false);
      setGroups([]);
      setValue4(null);
      setData([]);
      setGrapghData([]);
      setCameraWiseData([]);
      setError(false);
      return;
    }

    loadGroups(value3?.ID, nextSelectedLocations);
  };

  const handleButtonClick = () => {
    setError(false);
    let hasError = false;

    if (!value3) {
      setIsClientSelected(true);
      hasError = true;
    }

    if (!value1) {
      setIsMonthSelected(true);
      hasError = true;
    }

    if (!value2) {
      setIsYearSelected(true);
      hasError = true;
    }

    if (hasError) {
      setError(true);
      return;
    }

    if (value1 && value2 && value3) {
      const clientIdPayload = showLocationDropdown
        ? (selectedLocations.length > 0
            ? selectedLocations
            : locations.map((location) => Number(location.ID || location.id))).join(',')
        : value3.ID;

      const body = {
        month: value1.value,
        year: value2.value,
        client_id: clientIdPayload,
        group_id: value4 ? Number(value4.Id || value4.GroupId || 0) : 0,
      };

      setLoading(true);
      monthlyClientDashboard(body, (response) => {
        if (response.status === 200) {
          if (response.data.length > 0) {
            setNoData(false);
            setData(response.data);
            const apiData = response.data[0];
            setGrapghData(apiData.Details || []);
            setCameraWiseData(apiData.camera_wise_counts || []);
            setShowChart(true);
          } else {
            setData([]);
            setShowChart(false);
            setNoData(true);
            setGrapghData([]);
            setCameraWiseData([]);
          }
        }

        setLoading(false);
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={compactLayout ? 2 : 3}>
          <Autocomplete
            value={value3}
            onChange={(event, newValue) => {
              loadClientContext(newValue);
            }}
            options={uniqueClientOptions}
            getOptionLabel={(option) => `${option.displayname || ''}`}
            isOptionEqualToValue={(option, value) => option.ID === value.ID}
            renderOption={(props, option) => (
              <li {...props} key={option.ID}>
                {option.displayname}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Client"
                variant="outlined"
                error={error && isClientSelected}
              />
            )}
          />
          {error && isClientSelected && <Typography variant="caption" color="error">Select Client</Typography>}
        </Grid>

        {showLocationDropdown && (
          <Grid item xs={12} sm={compactLayout ? 2 : 3}>
            <Autocomplete
              fullWidth
              options={locations}
              value={selectedLocationOption}
              getOptionLabel={(option) => `${option.Location || option.name || ''}`}
              onChange={handleLocationChange}
              isOptionEqualToValue={(option, value) =>
                Number(option.ID || option.id) === Number(value.ID || value.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Location"
                  variant="outlined"
                />
              )}
            />
          </Grid>
        )}

        {showGroupDropdown && (
          <Grid item xs={12} sm={compactLayout ? 2 : 3}>
            <Autocomplete
              value={value4}
              onChange={(event, newValue) => {
                setValue4(newValue);
              }}
              options={groups}
              getOptionLabel={(option) => option.GroupName || ''}
              isOptionEqualToValue={(option, value) => Number(option.Id) === Number(value.Id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Group"
                  variant="outlined"
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={2}>
          <Autocomplete
            value={value1}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setIsMonthSelected(false);
                setValue1(newValue);
                setShowChart(false);
                setNoData(false);
              }
            }}
            options={months}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Month"
                variant="outlined"
                error={error && isMonthSelected}
              />
            )}
          />
          {error && isMonthSelected && <Typography variant="caption" color="error">Select Month</Typography>}
        </Grid>

        <Grid item xs={12} sm={2}>
          <Autocomplete
            value={value2}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setIsYearSelected(false);
                setValue2(newValue);
                setShowChart(false);
                setNoData(false);
              }
            }}
            options={years}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Year"
                variant="outlined"
                error={error && isYearSelected}
              />
            )}
          />
          {error && isYearSelected && <Typography variant="caption" color="error">Select Year</Typography>}
        </Grid>

        <Grid item xs={12} sm={compactLayout ? 2 : 2}>
          <Button variant="contained" onClick={handleButtonClick}>
            Generate
          </Button>
        </Grid>
      </Grid>

      {showChart && (
        <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormComponent
            barGraphData={graphData}
            cameraWiseData={cameraWiseData}
            Data={data}
            Month={value1.name}
            Year={value2.value}
            clientName={value3.displayname}
            clientId={value3.ID}
            Location={
              selectedLocations.length > 0
                ? locations
                    .filter((location) => selectedLocations.includes(location.ID || location.id))
                    .map((location) => location.Location)
                    .join(', ')
                : value3.Location
            }
          />
        </Grid>
      )}

      {!loading && noData && (
        <Grid container justifyContent="center">
          <Typography align="center" sx={{ fontWeight: 'bold', py: 2 }}>
            No Data
          </Typography>
        </Grid>
      )}

      {loading && (
        <Grid container justifyContent="center" sx={{ mt: 2 }}>
          <CircularProgress color="primary" />
        </Grid>
      )}
    </Box>
  );
};

export default MonthlyClientDashBoard;
